import React from 'react';
import { Box, Tooltip, alpha, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { palette } from '../../theme';

/**
 * Floating 3D navigation rail.
 * The panel is detached from the viewport edge and gains perspective depth on
 * hover; collapsed mode keeps icon-only navigation.
 */
export default function Sidebar({ items, secondary = [], collapsed, onToggle, role }) {
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const renderItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    const button = (
      <Box
        component={Link}
        to={item.path}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: collapsed ? 0 : 1.75,
          justifyContent: collapsed ? 'center' : 'flex-start',
          minHeight: 46,
          borderRadius: 2.5,
          position: 'relative',
          color: active ? palette.text : 'text.secondary',
          textDecoration: 'none',
          transition: 'background-color 220ms ease, color 220ms ease, transform 260ms cubic-bezier(0.22,0.9,0.28,1)',
          '&:hover': {
            color: palette.text,
            background: alpha(palette.primary, 0.1),
            transform: 'translateX(2px)',
          },
        }}
      >
        {active && (
          <Box
            sx={{
              position: 'absolute',
              left: collapsed ? -10 : -12,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 3,
              height: 22,
              borderRadius: 3,
              background: `linear-gradient(180deg, ${palette.primary}, ${palette.secondary})`,
              boxShadow: `0 0 12px ${alpha(palette.primary, 0.9)}`,
            }}
          />
        )}
        <Icon
          sx={{
            fontSize: 20,
            color: active ? palette.primary : 'inherit',
            filter: active ? `drop-shadow(0 0 8px ${alpha(palette.primary, 0.7)})` : 'none',
            transition: 'color 220ms ease',
          }}
        />
        {!collapsed && (
          <Typography sx={{ fontSize: '0.83rem', fontWeight: active ? 700 : 500, letterSpacing: '0.01em' }}>
            {item.label}
          </Typography>
        )}
      </Box>
    );

    return collapsed ? (
      <Tooltip key={item.key} title={item.label} placement="right" arrow>
        {button}
      </Tooltip>
    ) : (
      <Box key={item.key}>{button}</Box>
    );
  };

  return (
    <Box
      component="nav"
      sx={{
        position: { xs: 'relative', lg: 'fixed' },
        top: { xs: 0, lg: 18 },
        left: { xs: 0, lg: 18 },
        bottom: { xs: 0, lg: 18 },
        width: collapsed ? 78 : 250,
        zIndex: 1200,
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        borderRadius: 4,
        border: `1px solid ${alpha(palette.primary, 0.14)}`,
        background: `linear-gradient(180deg, ${alpha('#0C1428', 0.9)}, ${alpha('#050816', 0.92)})`,
        backdropFilter: 'blur(26px) saturate(140%)',
        WebkitBackdropFilter: 'blur(26px) saturate(140%)',
        boxShadow: '0 30px 70px -34px rgba(0,0,0,0.95), inset 0 1px 0 rgba(255,255,255,0.05)',
        transition: 'width 320ms cubic-bezier(0.22,0.9,0.28,1)',
        overflow: 'hidden',
      }}
    >
      {/* brand */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: collapsed ? 0 : 2.25, py: 2.25, justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: 2.25,
            display: 'grid',
            placeItems: 'center',
            background: `linear-gradient(140deg, ${palette.primary}, ${palette.secondary})`,
            boxShadow: `0 0 22px ${alpha(palette.primary, 0.55)}`,
          }}
        >
          <Box
            sx={{
              width: 13,
              height: 13,
              borderRadius: '50%',
              border: `2px solid ${alpha('#050816', 0.85)}`,
            }}
          />
        </Box>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.12em', lineHeight: 1.1 }}>
              BLUE NODE
            </Typography>
            <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary', letterSpacing: '0.22em' }}>
              COMMAND CENTER
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: collapsed ? 1.25 : 1.5, pb: 1.5 }}>
        {items.map(renderItem)}

        {secondary.length > 0 && (
          <>
            {!collapsed && (
              <Typography
                sx={{
                  mt: 2.5,
                  mb: 1,
                  px: 1.75,
                  fontSize: '0.58rem',
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  color: alpha(palette.secondaryText, 0.75),
                }}
              >
                PLATFORM
              </Typography>
            )}
            {collapsed && <Box sx={{ height: '1px', mx: 1, my: 2, background: alpha(palette.primary, 0.12) }} />}
            {secondary.map(renderItem)}
          </>
        )}
      </Box>

      {/* collapse control */}
      <Box sx={{ p: collapsed ? 1.25 : 1.5, borderTop: `1px solid ${alpha(palette.primary, 0.1)}` }}>
        <Box
          component="button"
          onClick={onToggle}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 1.25,
            py: 1.1,
            px: collapsed ? 0 : 1.75,
            borderRadius: 2.5,
            border: `1px solid ${alpha(palette.primary, 0.16)}`,
            background: alpha(palette.primary, 0.05),
            color: 'text.secondary',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '0.75rem',
            letterSpacing: '0.06em',
            transition: 'background-color 200ms ease, color 200ms ease',
            '&:hover': { background: alpha(palette.primary, 0.12), color: palette.text },
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: role === 'admin' ? palette.secondary : palette.success,
              boxShadow: `0 0 10px ${role === 'admin' ? palette.secondary : palette.success}`,
            }}
          />
          {!collapsed && <span>{role === 'admin' ? 'ADMIN CONSOLE' : 'OPERATOR CONSOLE'}</span>}
        </Box>
      </Box>
    </Box>
  );
}
